import React, { useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartDataset,
  LegendItem,
} from "chart.js";
import { isEqual } from "lodash";
import regression from "regression";
import {
  getPreferredFormattedTimeMs,
  formatSessionTime,
} from "../../../utils/preferences";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const LOW_POWER_COLOR = "#00ff59";
const HIGH_POWER_COLOR = "#ff00f2";

type QuartileType = "q1" | "q2" | "q3";

// Remove outliers using quartile threshold with a rolling window
function removeOutliers(
  points: { x: number; y: number; driver: string }[],
  quartile: QuartileType,
  windowMinutes: number,
) {
  if (points.length < 4) return points;

  const windowMs = windowMinutes * 60 * 1000;
  const quartileMultiplier =
    quartile === "q1" ? 0.25 : quartile === "q2" ? 0.5 : 0.75;

  return points.filter((point) => {
    // Get points within window of this point
    const windowPoints = points.filter(
      (p) => Math.abs(p.x - point.x) <= windowMs,
    );

    if (windowPoints.length < 4) return true; // Keep if not enough local data

    const yValues = windowPoints.map((p) => p.y).sort((a, b) => a - b);
    const qIndex = Math.floor(yValues.length * quartileMultiplier);
    const threshold = yValues[qIndex];

    return point.y <= threshold;
  });
}

// Convert line style string to borderDash array
const getLineDash = (lineStyle: string | undefined): number[] => {
  if (lineStyle?.toLowerCase() === "dash") return [10, 5];
  return [];
};

// Generate curve points from a regression result (with denormalization)
function generateCurvePoints(
  result: regression.Result,
  xMin: number,
  xMax: number,
  xMinOrig: number,
  xRange: number,
  numPoints = 100,
) {
  const points: { x: number; y: number }[] = [];
  const step = (xMax - xMin) / (numPoints - 1);
  for (let i = 0; i < numPoints; i++) {
    const xOrig = xMinOrig + (i * xRange) / (numPoints - 1);
    const xNorm = xMin + i * step;
    const [, y] = result.predict(xNorm);
    points.push({ x: xOrig, y });
  }
  return points;
}

export default function TrackEvolution() {
  const [polynomialOrder, setPolynomialOrder] = useState<number>(2);
  const [quartile, setQuartile] = useState<QuartileType>("q1");
  const [windowMinutes, setWindowMinutes] = useState<number>(5);

  const driverRuns = useSelector(
    (state: RootState) => state.fpAnalysis.driverRuns,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const sessionStatus = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status,
    isEqual,
  );

  const isFullWetSession = useSelector(
    (state: RootState) => state.fpAnalysis.isFullWetSession,
  );

  const highlightWetPeriod = useSelector(
    (state: RootState) => state.fpAnalysis.highlightWetPeriod,
  );

  const wetPeriodRange = useSelector(
    (state: RootState) => state.fpAnalysis.wetPeriodRange,
  );

  // Extract all push laps from driverRuns
  const { lowPowerPoints, highPowerPoints } = useMemo(() => {
    const isInWetPeriod = (startTime: number) => {
      if (!highlightWetPeriod) return false;
      const lapStartMinutes = startTime / 60000;
      return (
        lapStartMinutes >= wetPeriodRange[0] &&
        lapStartMinutes <= wetPeriodRange[1]
      );
    };
    const lowPower: { x: number; y: number; driver: string; isWet: boolean }[] =
      [];
    const highPower: {
      x: number;
      y: number;
      driver: string;
      isWet: boolean;
    }[] = [];

    Object.entries(driverRuns || {}).forEach(([driver, runs]) => {
      runs.forEach((run) => {
        if (!run.duration || !run.valid) return;

        // Skip pit/out/in laps
        if (
          run.type === "Pit" ||
          run.type === "Out" ||
          run.type === "In" ||
          run.type === "OutIn"
        ) {
          return;
        }

        const lapIsWet = isFullWetSession || isInWetPeriod(run.startTime);

        // In wet (full session or this lap in wet period), include all laps
        // In dry, only include push laps
        const shouldInclude =
          lapIsWet ||
          run.type === "HighPowerPush" ||
          run.type === "LowPowerPush";

        if (!shouldInclude) return;

        if (run.highPower) {
          highPower.push({
            x: run.startTime,
            y: run.duration,
            driver,
            isWet: lapIsWet,
          });
        } else {
          lowPower.push({
            x: run.startTime,
            y: run.duration,
            driver,
            isWet: lapIsWet,
          });
        }
      });
    });

    return {
      lowPowerPoints: lowPower.sort((a, b) => a.x - b.x),
      highPowerPoints: highPower.sort((a, b) => a.x - b.x),
    };
  }, [driverRuns, isFullWetSession, highlightWetPeriod, wetPeriodRange]);

  // If a session goes from wet to dry, we want to preserve the push lap filtering from before the wet period
  const chartData = useMemo(() => {
    // Separate wet and dry laps
    const dryLowPower = lowPowerPoints.filter((p) => !p.isWet);
    const wetLowPower = lowPowerPoints.filter((p) => p.isWet);
    const dryHighPower = highPowerPoints.filter((p) => !p.isWet);
    const wetHighPower = highPowerPoints.filter((p) => p.isWet);

    // Filter outliers from wet laps (always use Q1 and 1 min window for wet)
    const filteredWetLowPower = removeOutliers(wetLowPower, "q1", 1);
    const filteredWetHighPower = removeOutliers(wetHighPower, "q1", 1);

    // Combine: all dry + filtered wet (used for scatter display)
    const lowPowerDisplay = [...dryLowPower, ...filteredWetLowPower].sort(
      (a, b) => a.x - b.x,
    );
    const highPowerDisplay = [...dryHighPower, ...filteredWetHighPower].sort(
      (a, b) => a.x - b.x,
    );

    // Second outlier pass on combined data for regression
    const lowPowerRegression = removeOutliers(
      lowPowerDisplay,
      quartile,
      windowMinutes,
    );
    const highPowerRegression = removeOutliers(
      highPowerDisplay,
      quartile,
      windowMinutes,
    );

    // Calculate x ranges from regression data
    const lowXMin =
      lowPowerRegression.length > 0
        ? Math.min(...lowPowerRegression.map((p) => p.x))
        : 0;
    const lowXMax =
      lowPowerRegression.length > 0
        ? Math.max(...lowPowerRegression.map((p) => p.x))
        : 1;
    const lowXRange = lowXMax - lowXMin || 1;

    const highXMin =
      highPowerRegression.length > 0
        ? Math.min(...highPowerRegression.map((p) => p.x))
        : 0;
    const highXMax =
      highPowerRegression.length > 0
        ? Math.max(...highPowerRegression.map((p) => p.x))
        : 1;
    const highXRange = highXMax - highXMin || 1;

    // Normalize x values to 0-1 range for polynomial fitting
    const normalizeLowX = (x: number) => (x - lowXMin) / lowXRange;
    const normalizeHighX = (x: number) => (x - highXMin) / highXRange;

    // Fit polynomial curves using regression package (use filtered data)
    const lowPowerCurve =
      lowPowerRegression.length >= 3
        ? generateCurvePoints(
            regression.polynomial(
              lowPowerRegression.map(
                (p) => [normalizeLowX(p.x), p.y] as [number, number],
              ),
              { order: polynomialOrder },
            ),
            0,
            1,
            lowXMin,
            lowXRange,
            100,
          )
        : [];

    const highPowerCurve =
      highPowerRegression.length >= 3
        ? generateCurvePoints(
            regression.polynomial(
              highPowerRegression.map(
                (p) => [normalizeHighX(p.x), p.y] as [number, number],
              ),
              { order: polynomialOrder },
            ),
            0,
            1,
            highXMin,
            highXRange,
            100,
          )
        : [];

    // Create datasets for Jag driver connected lines
    const jagDriverDatasets: ChartDataset<"line">[] = [];
    const jagLegendItems: {
      name: string;
      color: string;
      lineDash: number[];
    }[] = [];

    // Find all Jaguar drivers and add their datasets
    const allDriverIds = [
      ...new Set([
        ...lowPowerPoints.map((p) => p.driver),
        ...highPowerPoints.map((p) => p.driver),
      ]),
    ];

    allDriverIds.forEach((driverId) => {
      const info = driverInfo?.[driverId];
      const isJaguar =
        info?.driverTeam?.toLowerCase().includes("jaguar") ?? false;

      if (!isJaguar) return;

      const driverName = info?.driverName || driverId;
      const driverColor =
        info?.driverHighlightColourAlt || info?.driverTeamColour || "#008080";
      const lineDash = getLineDash(info?.driverLineStyle);

      // Track for legend
      jagLegendItems.push({ name: driverName, color: driverColor, lineDash });

      // Use display points
      const lowPowerDriverPoints = lowPowerDisplay
        .filter((p) => p.driver === driverId)
        .sort((a, b) => a.x - b.x);

      const highPowerDriverPoints = highPowerDisplay
        .filter((p) => p.driver === driverId)
        .sort((a, b) => a.x - b.x);

      if (lowPowerDriverPoints.length >= 1) {
        jagDriverDatasets.push({
          label: `${driverName} (Low)`,
          data: lowPowerDriverPoints.map((p) => ({
            x: p.x,
            y: p.y,
            driverName,
          })),
          borderColor: driverColor,
          backgroundColor: driverColor,
          pointRadius: 4,
          pointHitRadius: 0,
          borderWidth: 2,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1,
          borderDash: lineDash,
          showLine: true,
          tension: 0,
        });
      }

      if (highPowerDriverPoints.length >= 1) {
        jagDriverDatasets.push({
          label: `${driverName} (High)`,
          data: highPowerDriverPoints.map((p) => ({
            x: p.x,
            y: p.y,
            driverName,
          })),
          borderColor: driverColor,
          backgroundColor: driverColor,
          pointRadius: 4,
          pointHitRadius: 0,
          borderWidth: 2,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1,
          borderDash: lineDash,
          showLine: true,
          tension: 0,
        });
      }
    });

    return {
      datasets: [
        // Regression curve - low power
        {
          label: "Low Power Trend",
          data: lowPowerCurve,
          borderColor: LOW_POWER_COLOR,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHitRadius: 0,
          borderWidth: 3,
          showLine: true,
        },
        // Regression curve - high power
        {
          label: "High Power Trend",
          data: highPowerCurve,
          borderColor: HIGH_POWER_COLOR,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHitRadius: 0,
          borderWidth: 3,
          showLine: true,
        },
        {
          label: "Low Power Trend BG",
          data: lowPowerCurve,
          borderColor: "#ffffff",
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHitRadius: 0,
          borderWidth: 7,
          showLine: true,
        },
        {
          label: "High Power Trend BG",
          data: highPowerCurve,
          borderColor: "#ffffff",
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHitRadius: 0,
          borderWidth: 7,
          showLine: true,
        },
        // Jag driver connected lines
        ...jagDriverDatasets,
        // Scatter points - low power
        {
          label: "Low Power Push Laps",
          data: lowPowerDisplay.map((p) => ({
            x: p.x,
            y: p.y,
            driverName: driverInfo?.[p.driver]?.driverName || p.driver,
          })),
          borderColor: LOW_POWER_COLOR,
          backgroundColor: "rgba(0, 255, 89, 0.3)",
          pointRadius: 4,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1,
          showLine: false,
        },
        // Scatter points - high power
        {
          label: "High Power Push Laps",
          data: highPowerDisplay.map((p) => ({
            x: p.x,
            y: p.y,
            driverName: driverInfo?.[p.driver]?.driverName || p.driver,
          })),
          borderColor: HIGH_POWER_COLOR,
          backgroundColor: "rgba(255, 0, 242, 0.3)",
          pointRadius: 4,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1,
          showLine: false,
        },
      ],
      jagLegendItems,
      lowPowerDisplay,
      highPowerDisplay,
    };
  }, [
    lowPowerPoints,
    highPowerPoints,
    driverInfo,
    polynomialOrder,
    quartile,
    windowMinutes,
  ]);

  const { lowPowerDisplay, highPowerDisplay } = chartData;
  const { jagLegendItems: jagLegend } = chartData;

  const chartOptions: ChartOptions<"line"> = useMemo(() => {
    // Compute y-axis range from displayed data
    const allLapTimes = [
      ...lowPowerDisplay.map((e) => e.y),
      ...highPowerDisplay.map((e) => e.y),
    ];

    const minLapTime =
      allLapTimes.length > 0 ? Math.min(...allLapTimes) : 60000;
    const maxLapTime =
      allLapTimes.length > 0 ? Math.max(...allLapTimes) : 90000;
    const padding = (maxLapTime - minLapTime) * 0.1 || 1000;

    // Compute x-axis range: 0 to session duration or last data point + padding
    const sessionDurationMs = (sessionStatus?.finalTime || 2400) * 1000; // default 40 min
    const allSessionTimes = [
      ...lowPowerDisplay.map((e) => e.x),
      ...highPowerDisplay.map((e) => e.x),
    ];
    const lastDataPointTime =
      allSessionTimes.length > 0 ? Math.max(...allSessionTimes) : 0;
    const xPadding = 60000; // 1 minute padding
    const maxX = Math.max(sessionDurationMs, lastDataPointTime + xPadding);

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: true,
          position: "bottom" as const,
          // onClick: () => {},
          labels: {
            color: "#ffffff",
            font: {
              weight: "bold" as const,
            },
            usePointStyle: true,
            generateLabels: (): LegendItem[] => {
              const labels: LegendItem[] = [];

              // Low Power trend
              labels.push({
                text: "Low Power",
                fontColor: "#ffffff",
                strokeStyle: LOW_POWER_COLOR,
                fillStyle: LOW_POWER_COLOR,
                lineWidth: 3,
                pointStyle: "line" as const,
              });

              // High Power trend
              labels.push({
                text: "High Power",
                fontColor: "#ffffff",
                strokeStyle: HIGH_POWER_COLOR,
                fillStyle: HIGH_POWER_COLOR,
                lineWidth: 3,
                pointStyle: "line" as const,
              });

              // Jag drivers
              jagLegend.forEach((jag) => {
                labels.push({
                  text: jag.name,
                  fontColor: "#ffffff",
                  strokeStyle: jag.color,
                  fillStyle: jag.color,
                  lineWidth: 2,
                  lineDash: jag.lineDash,
                  pointStyle: "line" as const,
                });
              });

              return labels;
            },
          },
        },
        title: {
          display: true,
          text: "Track Evolution",
          color: "#ffffff",
          font: { size: 18, weight: "bold" as const },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const dataPoint = context.raw as {
                x: number;
                y: number;
                driverName?: string;
              };
              const lapTime = getPreferredFormattedTimeMs(dataPoint.y);

              if (context.dataset.label?.includes("Trend")) {
                return `${context.dataset.label}: ${lapTime}`;
              }

              return `${context.dataset.label}: ${lapTime} (${
                dataPoint.driverName || ""
              })`;
            },
            title: (contexts) => {
              const dataPoint = contexts[0]?.raw as { x: number; y: number };
              if (dataPoint) {
                return `Session: ${formatSessionTime(dataPoint.x)}`;
              }
              return "";
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: 0,
          max: maxX,
          title: {
            display: true,
            text: "Session Time",
            color: "#aaaaaa",
          },
          ticks: {
            color: "#aaaaaa",
            callback: (value) => formatSessionTime(value as number),
          },
          grid: {
            color: "#444444",
          },
        },
        y: {
          type: "linear" as const,
          title: {
            display: true,
            text: "Lap Time",
            color: "#aaaaaa",
          },
          min: minLapTime - padding,
          max: maxLapTime + padding,
          reverse: false,
          ticks: {
            color: "#aaaaaa",
            callback: (value) => getPreferredFormattedTimeMs(value as number),
          },
          grid: {
            color: "#444444",
          },
        },
      },
    };
  }, [lowPowerDisplay, highPowerDisplay, sessionStatus, jagLegend]);

  const hasData = lowPowerPoints.length > 0 || highPowerPoints.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.controlsRow}>
        <span>Poly order:</span>
        <select
          className={styles.configSelector}
          value={polynomialOrder}
          onChange={(e) => setPolynomialOrder(parseInt(e.target.value))}
          disabled={!hasData}
        >
          <option value={1}>Order 1</option>
          <option value={2}>Order 2</option>
          <option value={3}>Order 3</option>
          <option value={4}>Order 4</option>
        </select>
        <span>Fit lower than:</span>
        <select
          className={styles.configSelector}
          value={quartile}
          onChange={(e) => setQuartile(e.target.value as QuartileType)}
          disabled={!hasData}
        >
          <option value="q1">Q1</option>
          <option value="q2">Q2</option>
          <option value="q3">Q3</option>
        </select>
        <span>Window:</span>
        <select
          className={styles.configSelector}
          value={windowMinutes}
          onChange={(e) => setWindowMinutes(parseInt(e.target.value))}
          disabled={!hasData}
        >
          <option value={1}>1 min</option>
          <option value={5}>5 min</option>
          <option value={10}>10 min</option>
        </select>
      </div>
      <div className={styles.chartWrapper}>
        {hasData ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div className={styles.noData}>No track evolution data yet</div>
        )}
      </div>
    </div>
  );
}
